import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormControlLabel, Switch, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { getCelsiusFromFahrenheight, getConvertedTemperature, getTemperatureUnitSymbol } from "app/tools/temperature"
import { IFormConfigurationSchedule } from "form/model"
import { getWeatherIcon } from "form/tools/schedule"
import { checkTouched } from "form/tools/validation"
import { Field, FieldProps, FormikErrors } from "formik"
import React from "react"
import { ALL_WEATHERS, WEATHERS } from "schedule/model"
import { FlexBox } from "ui/components/FlexBox"
import { isCelsiusTemperatureUnit } from "user/tools/common"
import { FieldText } from "./FieldText"

interface IFieldWeathersProps {
  disabled?: boolean
  weathers: WEATHERS[]
  min_celsius?: number | null
  max_celsius?: number | null
  scheduleErrors?: FormikErrors<IFormConfigurationSchedule>
  sx?: any
}

interface IWeatherProps {
  weather: WEATHERS
  isActive: boolean
  disabled?: boolean
  setValue: (weather: WEATHERS, toAdd: boolean) => void
}

interface IFieldTemperatureProps {
  fieldName: string
  label: string
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  disabled?: boolean
  value: number | null
  error?: string
}

const FIELD_WEATHERS_NAME: string = "schedule.weathers"
const FIELD_MIN_TEMP_NAME: string = "schedule.min_celsius"
const FIELD_MAX_TEMP_NAME: string = "schedule.max_celsius"

const Weather = React.memo(({ weather, isActive, setValue, disabled }: IWeatherProps) => (
  <FormControlLabel
    id={`weather-field-${weather}-container`}
    disabled={disabled}
    sx={{
      "& .colorized": {
        color: theme => (isActive ? theme.palette.primary.main : theme.palette.text.secondary)
      },
      "&:hover .colorized": {
        color: theme =>
          isActive ? theme.palette.primary.main : !disabled ? theme.palette.text.primary : theme.palette.text.secondary
      },
      "& span.Mui-checked span.MuiSwitch-thumb": {
        backgroundColor: theme => theme.palette.primary.main
      }
    }}
    control={
      <Switch
        id={`weather-field-${weather}`}
        checked={isActive}
        value={isActive}
        onChange={(_event, checked) => setValue(weather, checked)}
        disabled={disabled}
      />
    }
    label={
      <FlexBox
        id={`weather-field-${weather}-label-content`}
        vertical
        sx={{
          "gap": ".2rem",
          "& .weather-icon": {
            fontSize: "1.5rem"
          }
        }}>
        <Typography id={`weather-label-${weather}`} fontSize=".8rem" className="colorized">
          {translate(`label.weather.${weather}`)}
        </Typography>
        <FontAwesomeIcon icon={getWeatherIcon(weather)} className="weather-icon colorized" />
      </FlexBox>
    }
    labelPlacement="end"
  />
))

const FieldTemperature = React.memo(
  ({ fieldName, label, disabled, value, error, setFieldValue }: IFieldTemperatureProps) => (
    <FlexBox
      ai="start"
      sx={{
        "width": "9rem",
        "& fieldset": {
          borderLeft: "none !important"
        }
      }}>
      <FlexBox
        sx={{
          backgroundColor: theme => (error ? theme.palette.error.main : theme.palette.form),
          mr: "-.2rem",
          zIndex: 2,
          opacity: disabled ? 0.6 : 1,
          pl: ".5rem",
          pr: ".5rem",
          borderTopLeftRadius: ".25rem",
          borderBottomLeftRadius: ".25rem",
          height: "2.5rem",
          transition: "opacity 400ms ease"
        }}>
        <Typography
          sx={{
            color: theme => (error ? theme.palette.text.primary : theme.palette.formText),
            fontSize: ".8rem"
          }}>
          {getTemperatureUnitSymbol()}
        </Typography>
      </FlexBox>
      <FieldText
        fieldName={fieldName}
        label={label}
        error={error}
        value={getConvertedTemperature(value)}
        disabled={disabled}
        isNumber
        clearable
        roundValue
        beforeChange={newValue =>
          newValue === "" || newValue === null
            ? null
            : isCelsiusTemperatureUnit()
              ? newValue
              : getCelsiusFromFahrenheight(newValue)
        }
      />
    </FlexBox>
  )
)

export const FieldWeathers = ({
  disabled,
  weathers,
  scheduleErrors,
  min_celsius,
  max_celsius,
  sx
}: IFieldWeathersProps) => (
  <Field name={FIELD_WEATHERS_NAME}>
    {({ form: { touched, setFieldValue } }: FieldProps) => (
      <FlexBox
        id="weather-selection"
        wrap
        fullWidth
        jc="space-evenly"
        sx={{
          "gap": "2rem",
          "@media (max-width: 1100px)": { "& > *": { width: "100% !important" } }
        }}>
        <FlexBox
          jc="start"
          wrap
          sx={{
            mt: "1rem",
            gap: "2rem",
            ...sx
          }}>
          {ALL_WEATHERS.map((w, index) => (
            <Weather
              key={index}
              isActive={weathers.includes(w)}
              disabled={disabled}
              weather={w}
              setValue={(selectedWeather: WEATHERS, toAdd: boolean) => {
                checkTouched(touched, FIELD_WEATHERS_NAME)
                const newWeaters = weathers.filter(weather => weather !== selectedWeather)
                if (toAdd) {
                  newWeaters.push(selectedWeather)
                }
                setFieldValue(FIELD_WEATHERS_NAME, newWeaters)
              }}
            />
          ))}
        </FlexBox>
        <FlexBox vertical ai="start" sx={{ gap: ".6rem" }}>
          <Typography color="textPrimary" fontSize=".8rem">
            {translate("label.temperature")}
          </Typography>
          <FieldTemperature
            fieldName={FIELD_MIN_TEMP_NAME}
            label={translate("label.under")}
            error={scheduleErrors?.min_celsius}
            value={min_celsius as number}
            disabled={disabled}
            setFieldValue={setFieldValue}
          />
          <FieldTemperature
            fieldName={FIELD_MAX_TEMP_NAME}
            label={translate("label.above")}
            error={scheduleErrors?.max_celsius}
            value={max_celsius as number}
            disabled={disabled}
            setFieldValue={setFieldValue}
          />
        </FlexBox>
      </FlexBox>
    )}
  </Field>
)
